/**
 * ViewStateContext — lightweight registry for shareable view state.
 *
 * Components register snapshot functions that return their current state.
 * ShareButton calls `getFullViewState()` to capture everything at once.
 * ShareView/restore calls `restoreViewState()` to apply a saved snapshot.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

export type ViewStateSlice = Record<string, unknown>;
type SnapshotFn = () => ViewStateSlice;
type RestoreFn = (state: ViewStateSlice) => void;

interface Registration {
  snapshot: SnapshotFn;
  restore: RestoreFn;
}

interface ViewStateContextValue {
  /** Register a component's state snapshot + restore functions. Returns unregister. */
  register: (key: string, snapshot: SnapshotFn, restore: RestoreFn) => () => void;
  /** Collect current state from all registered components. */
  getFullViewState: () => Record<string, ViewStateSlice>;
  /** Restore state to all registered components. */
  restoreViewState: (state: Record<string, ViewStateSlice>) => void;
}

const ViewStateCtx = createContext<ViewStateContextValue | null>(null);

export function useViewState() {
  const ctx = useContext(ViewStateCtx);
  if (!ctx) throw new Error("useViewState must be inside ViewStateProvider");
  return ctx;
}

export function ViewStateProvider({ children }: { children: ReactNode }) {
  const registry = useRef<Map<string, Registration>>(new Map());

  const register = useCallback((key: string, snapshot: SnapshotFn, restore: RestoreFn) => {
    registry.current.set(key, { snapshot, restore });
    return () => { registry.current.delete(key); };
  }, []);

  const getFullViewState = useCallback(() => {
    const state: Record<string, ViewStateSlice> = {};
    for (const [key, reg] of registry.current) {
      state[key] = reg.snapshot();
    }
    return state;
  }, []);

  const restoreViewState = useCallback((state: Record<string, ViewStateSlice>) => {
    for (const [key, slice] of Object.entries(state)) {
      const reg = registry.current.get(key);
      if (reg) reg.restore(slice);
    }
  }, []);

  // Auto-restore from sessionStorage when arriving from a share link
  const [searchParams, setSearchParams] = useSearchParams();
  const [restored, setRestored] = useState(false);

  useEffect(() => {
    if (restored) return;
    if (searchParams.get("_shared") !== "1") return;

    const SHARED_KEY = "thread-shared-view-state";
    const raw = sessionStorage.getItem(SHARED_KEY);
    if (!raw) { setRestored(true); return; }

    sessionStorage.removeItem(SHARED_KEY);

    // Delay restore slightly so components have time to register
    const timer = setTimeout(() => {
      try {
        const state = JSON.parse(raw) as Record<string, ViewStateSlice>;
        restoreViewState(state);
      } catch { /* malformed data — ignore */ }
      // Clean the _shared flag from URL
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("_shared");
        return next;
      }, { replace: true });
      setRestored(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchParams, setSearchParams, restoreViewState, restored]);

  return (
    <ViewStateCtx.Provider value={{ register, getFullViewState, restoreViewState }}>
      {children}
    </ViewStateCtx.Provider>
  );
}
